import { DivConst } from 'constants/divConst';

import { useApiState } from 'hooks/useApiState';
import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import type { AcctStateType } from 'stores/atoms/acctState';
import type { FormReportRefResponseType } from 'types/api/formReportRefResponseType';
import { getDefaultReportDateRange } from 'pages/ws/utils/reportDateRange';
import { openProductionUrl } from 'utils/form';

export interface UseWsReportFormResult {
  initialized: boolean;
  processing: boolean;
  form: FormReportRefResponseType | null;
  reportDateFrom: string;
  reportDateTo: string;
  isExpiredPlan: boolean;
  modalTermOpen: boolean;
  setModalTermOpen: (open: boolean) => void;
  modalDownloadOpen: boolean;
  setModalDownloadOpen: (open: boolean) => void;
  onContentOpen: () => void;
  onModalReportDownloadOpen: () => void;
  onModalReportTermOpen: () => void;
  onChangeReportTerm: (reportDateFrom: string, reportDateTo: string) => void;
}

export function useWsReportForm(
  formCd: string | undefined,
  company: NonNullable<AcctStateType['company']>
): UseWsReportFormResult {
  const { postFormReportRef } = useApiState();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();

  const [initialized, setInitialized] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [form, setForm] = useState<FormReportRefResponseType | null>(null);
  const [reportDateFrom, setReportDateFrom] = useState('');
  const [reportDateTo, setReportDateTo] = useState('');
  const [modalTermOpen, setModalTermOpen] = useState(false);
  const [modalDownloadOpen, setModalDownloadOpen] = useState(false);

  const isExpiredPlan = company.plan.isExpired;

  const initState = async () => {
    try {
      const { reportDateFrom: from, reportDateTo: to } = getDefaultReportDateRange(new Date());

      const response = await postFormReportRef({
        formCd,
        reportDateFrom: from,
        reportDateTo: to,
      });

      setReportDateFrom(from);
      setReportDateTo(to);
      setForm(response);
    } catch (e: unknown) {
      showBoundary(e);
    } finally {
      setInitialized(true);
    }
  };

  useEffect(() => {
    // ステートの初期化
    // initState は useCallback 化されていないため render ごとに参照が変わる。
    // マウント時の 1 回だけ実行するため意図的に空配列を使用する。
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 初回マウント時の状態初期化のため意図的
    initState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * コンテンツ確認ボタン押下時の処理
   */
  const onContentOpen = () => {
    if (form.formStatusDiv === DivConst.items.formStatus.published.value) {
      openProductionUrl(form.cd);
    } else {
      navigate(`/ws/${company.cd}/form/edit/${form.cd}`);
    }
  };

  /**
   * レポート出力ボタン押下時の処理
   */
  const onModalReportDownloadOpen = () => {
    if (isExpiredPlan) {
      // プラン（利用期間切れ）の場合は処理しない
      return;
    }
    setModalDownloadOpen(true);
  };

  /**
   * 集計期間変更ボタン押下時の処理
   */
  const onModalReportTermOpen = () => {
    setModalTermOpen(true);
  };

  /**
   * 集計期間変更モーダルで期間を変更した場合の処理
   */
  const onChangeReportTerm = async (from: string, to: string) => {
    try {
      setProcessing(true);

      const response = await postFormReportRef({
        formCd,
        reportDateFrom: from,
        reportDateTo: to,
      });

      setReportDateFrom(from);
      setReportDateTo(to);
      setForm(response);
    } catch (e: unknown) {
      showBoundary(e);
    } finally {
      setProcessing(false);
    }
  };

  return {
    initialized,
    processing,
    form,
    reportDateFrom,
    reportDateTo,
    isExpiredPlan,
    modalTermOpen,
    setModalTermOpen,
    modalDownloadOpen,
    setModalDownloadOpen,
    onContentOpen,
    onModalReportDownloadOpen,
    onModalReportTermOpen,
    onChangeReportTerm,
  };
}
